import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuLabel,
} from '@/components/ui/dropdown-menu';
import {
  Bell,
  Search,
  Menu,
  Settings,
  LogOut,
  User,
  AlertCircle,
  Shield,
  Key,
  UserCheck,
  CreditCard
} from 'lucide-react';
import { useAuth } from '@/auth/authContext';
import { ThemeToggle } from '@/components/theme/ThemeToggle';

const EnhancedHeader = ({ setSidebarOpen }) => {
  const navigate = useNavigate();
  const { user, logout, hasPermission } = useAuth();

  const getUserInitials = (firstName?: string, lastName?: string) => {
    if (!firstName || !lastName) return 'U';
    return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
  };

  const getRoleDisplayName = (role?: string) => {
    if (!role) return 'User';
    return role.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  const getRoleBadgeColor = (role?: string) => {
    switch (role) {
      case 'super_admin':
        return 'bg-gradient-to-r from-purple-500 to-pink-500 text-white';
      case 'admin':
        return 'bg-gradient-to-r from-blue-500 to-cyan-500 text-white';
      case 'driver':
        return 'bg-gradient-to-r from-green-500 to-emerald-500 text-white';
      case 'passenger':
        return 'bg-gradient-to-r from-orange-500 to-yellow-500 text-white';
      default:
        return 'bg-muted text-muted-foreground';
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const menuItems = [
    {
      icon: <User className="w-4 h-4" />,
      label: 'Profile',
      action: () => navigate('/admin/profile'),
      show: true
    },
    {
      icon: <Key className="w-4 h-4" />,
      label: 'Security Settings',
      action: () => navigate('/admin/security/change-password'),
      show: true
    },
    {
      icon: <UserCheck className="w-4 h-4" />,
      label: 'User Management',
      action: () => navigate('/admin/users/list'),
      show: hasPermission('user', 'read')
    },
    {
      icon: <CreditCard className="w-4 h-4" />,
      label: 'Billing',
      action: () => navigate('/admin/billing'),
      show: hasPermission('financial', 'read_transactions')
    },
    {
      icon: <Settings className="w-4 h-4" />,
      label: 'Settings',
      action: () => navigate('/admin/settings'),
      show: hasPermission('system', 'system_settings')
    }
  ].filter(item => item.show);

  return (
    <header className="bg-card border-b border-border px-6 py-4">
      <div className="flex items-center justify-between">
        {/* Left side */}
        <div className="flex items-center space-x-4">
          {/* Mobile Menu Button */}
          <Button
            variant="ghost"
            size="sm"
            className="lg:hidden"
            onClick={() => setSidebarOpen(true)}
          >
            <Menu className="w-5 h-5" />
          </Button>

          {/* Mobile Logo */}
          <div
            className="lg:hidden flex items-center cursor-pointer hover:opacity-80 transition-opacity"
            onClick={() => navigate('/admin/dashboard')}
          >
            <img
              src="/assets/logo/Aboosto-03.svg"
              alt="Aboosto"
              className="h-8 w-8"
              onError={(e) => {
                const target = e.target as HTMLImageElement;
                target.src = '/assets/logo/Aboosto-03.png';
              }}
            />
          </div>

          {/* Aboosto Logo */}
          <div
            className="hidden sm:flex items-center space-x-3 cursor-pointer hover:opacity-80 transition-opacity"
            onClick={() => navigate('/admin/dashboard')}
          >
            <div className="relative">
              <img
                src="/assets/logo/Aboosto-03.svg"
                alt="Aboosto"
                className="h-10 w-10"
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  target.src = '/assets/logo/Aboosto-03.png';
                }}
              />
            </div>
          </div>

          <div className="relative hidden sm:block">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search drivers, passengers, trips..."
              className="pl-10 w-64 lg:w-80"
            />
          </div>
        </div>

        {/* Right side */}
        <div className="flex items-center space-x-4">
          {/* Theme Toggle */}
          <ThemeToggle />

          {/* Notifications */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" className="relative">
                <Bell className="w-5 h-5" />
                <Badge
                  variant="destructive"
                  className="absolute -top-1 -right-1 w-5 h-5 flex items-center justify-center p-0 text-xs"
                >
                  3
                </Badge>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-80">
              <div className="p-2">
                <h4 className="font-semibold text-sm mb-2">Notifications</h4>
                <div className="space-y-2">
                  <div className="flex items-start space-x-2 p-2 rounded-lg bg-muted/50 cursor-pointer hover:bg-muted transition-colors">
                    <AlertCircle className="w-4 h-4 text-warning mt-0.5" />
                    <div className="flex-1">
                      <p className="text-xs font-medium">Driver License Expiring</p>
                      <p className="text-xs text-muted-foreground">John Smith's license expires in 3 days</p>
                      <p className="text-xs text-muted-foreground mt-1">2 hours ago</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-2 p-2 rounded-lg bg-muted/50 cursor-pointer hover:bg-muted transition-colors">
                    <AlertCircle className="w-4 h-4 text-destructive mt-0.5" />
                    <div className="flex-1">
                      <p className="text-xs font-medium">New Dispute Filed</p>
                      <p className="text-xs text-muted-foreground">Passenger complaint about ride #12345</p>
                      <p className="text-xs text-muted-foreground mt-1">5 hours ago</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-2 p-2 rounded-lg bg-muted/50 cursor-pointer hover:bg-muted transition-colors">
                    <AlertCircle className="w-4 h-4 text-success mt-0.5" />
                    <div className="flex-1">
                      <p className="text-xs font-medium">Peak Hours Alert</p>
                      <p className="text-xs text-muted-foreground">High demand detected in downtown area</p>
                      <p className="text-xs text-muted-foreground mt-1">1 day ago</p>
                    </div>
                  </div>
                </div>
                <div className="mt-3 pt-2 border-t">
                  <Button variant="ghost" size="sm" className="w-full justify-start">
                    View all notifications
                  </Button>
                </div>
              </div>
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Profile Menu */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" className="flex items-center space-x-2 h-auto p-1">
                <div className="relative">
                  <Avatar className="w-8 h-8">
                    <AvatarImage src={user?.profilePicture} alt={user?.firstName} />
                    <AvatarFallback className="bg-primary text-primary-foreground text-sm">
                      {getUserInitials(user?.firstName, user?.lastName)}
                    </AvatarFallback>
                  </Avatar>
                  {/* Status indicator */}
                  <div className={`absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full border-2 border-background ${
                    user?.status === 'active' ? 'bg-green-500' :
                    user?.status === 'inactive' ? 'bg-gray-400' :
                    user?.status === 'suspended' ? 'bg-red-500' :
                    'bg-yellow-500'
                  }`} />
                </div>
                <div className="hidden sm:block text-left">
                  <div className="text-sm font-medium text-foreground">
                    {user?.firstName} {user?.lastName}
                  </div>
                  <div className="flex items-center space-x-2">
                    <Badge className={`text-xs ${getRoleBadgeColor(user?.role)}`}>
                      {getRoleDisplayName(user?.role)}
                    </Badge>
                  </div>
                </div>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel className="flex items-center space-x-2">
                <Shield className="w-4 h-4" />
                <div>
                  <div className="font-medium">Account</div>
                  <div className="text-xs text-muted-foreground">
                    {user?.email}
                  </div>
                </div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />

              {menuItems.map((item, index) => (
                <DropdownMenuItem
                  key={index}
                  onClick={item.action}
                  className="cursor-pointer"
                >
                  {item.icon}
                  <span className="ml-2">{item.label}</span>
                </DropdownMenuItem>
              ))}

              {menuItems.length > 0 && <DropdownMenuSeparator />}

              <DropdownMenuItem
                onClick={() => window.open('/help', '_blank')}
                className="cursor-pointer"
              >
                <AlertCircle className="w-4 h-4 mr-2" />
                Help & Support
              </DropdownMenuItem>

              <DropdownMenuSeparator />

              <DropdownMenuItem
                onClick={handleLogout}
                className="cursor-pointer text-red-600 focus:text-red-600"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Sign out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </header>
  );
};

export default EnhancedHeader;